import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconAlertTriangle,
} from '@tabler/icons-react';
import React from 'react';

// Mantine color mapping
const intentToColor: Record<string, string> = {
  primary: 'blue',
  success: 'green',
  warning: 'yellow',
  danger: 'red',
  none: 'gray',
};

// Blueprint icon to Mantine icon mapping
const iconMap: Record<string, React.ReactNode> = {
  'tick-circle': <IconCheck size={20} />,
  tick: <IconCheck size={20} />,
  'warning-sign': <IconAlertTriangle size={20} />,
  'info-sign': <IconInfoCircle size={20} />,
  error: <IconAlertCircle size={20} />,
  updated: <IconCheck size={20} />,
};

// blueprint ToastProps interface
interface BlueprintToastProps {
  message: React.ReactNode;
  intent?: 'primary' | 'success' | 'warning' | 'danger' | 'none';
  icon?: string | React.ReactNode;
  timeout?: number;
  action?: {
    onClick: () => void;
    text: string;
  };
}

// position types
type MantinePosition =
  | 'top-left'
  | 'top-right'
  | 'top-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom-center';

class ToastAdapter {
  private position: MantinePosition;

  constructor(position: MantinePosition) {
    this.position = position;
  }

  show(props: BlueprintToastProps): string {
    const { message, intent = 'none', icon, timeout = 3000, action } = props;

    const color = intentToColor[intent] || 'gray';

    let mantineIcon: React.ReactNode | undefined;
    if (icon) {
      if (typeof icon === 'string') {
        mantineIcon = iconMap[icon];
      } else {
        mantineIcon = icon;
      }
    }

    // Show the notification
    const id = notifications.show({
      message: typeof message === 'string' ? message : <>{message}</>,
      color,
      icon: mantineIcon,
      autoClose: timeout,
      position: this.position,
      withCloseButton: true,
      ...(action && {
        onClose: action.onClick,
      }),
    });

    return id;
  }

  dismiss(id: string): void {
    notifications.hide(id);
  }

  clear(): void {
    notifications.clean();
  }
}

/** @registry Single top-center Mantine notifications adapter (Blueprint-shaped API); use showToast/showErrorToast/showSuccessToast instead of calling this directly. */
export const AppToast = new ToastAdapter('top-center');
